// The box a sign-up moment shows (#5195; founder pick A, "In place, right
// under the value"). One component for every moment: a heading, one free
// line (the taste), what a free account adds as a list, one button that
// names the payoff, and "Not now". The words come from the moment's entry in
// content/signupMoments.js (MOMENTS[id].box), never typed here.
//
// Events: signup_moment {state: shown} once when half of it is on screen,
// {state: clicked} on the button (then `onGo()`, which opens the sign-in
// sheet with the button's words), {state: dismissed} on Not now (the moment
// stays dismissed for the visit). `props`: the event's band-level props.
//
// Accessibility (design.md section 5): a section with its heading, the items
// a list, both actions real buttons named by their words, 44 px targets; it
// sits inside the read's own aria-live region, so it is announced after the
// read, and focus never moves when it appears.
import React, { useRef, useState } from 'react';
import useShownOnce from '../../hooks/useShownOnce';
import { track, EVENTS } from '../../services/analytics';
import { REPLAY_PRIVATE_CLASS } from '../../services/analyticsPrivacy';
import { dismissMoment, isDismissed } from '../../services/signupMoments';
import { COPY, MOMENTS } from '../../content/signupMoments';

// `children`: drawn under the heading (the comparing strip's school chips).
// `extra`: a second, quiet action beside Not now (the strip's "Check with my
// grades"), {label, onClick}.
export default function MomentBox({ moment, box, props = {}, onGo, testid = 'signup-moment', className = '', children = null,
    extra = null }) {
    const ref = useRef(null);
    const [gone, setGone] = useState(() => isDismissed(moment));
    const live = Boolean(box) && !gone;
    const evt = (state) => track(EVENTS.SIGNUP_MOMENT, { moment, state, payoff: box?.payoff, variant: 'A', ...props });
    useShownOnce(ref, live, () => evt('shown'));
    if (!live) return null;
    const headingId = `${testid}-heading`;

    return (
        <section ref={ref} data-testid={testid} data-moment={moment} aria-labelledby={headingId}
            className={`${REPLAY_PRIVATE_CLASS} rounded-xl border border-[#9DBBA6] bg-white p-4 text-left ${className}`}>
            <h3 id={headingId} className="text-sm font-semibold text-[#1A4D2E]">{box.heading}</h3>
            {children}
            {box.free && <p data-testid={`${testid}-free`} className="mt-1 text-sm text-[#2C2C2C]">{box.free}</p>}
            {box.items.length > 0 && (
                <>
                    <p className="mt-3 text-xs font-semibold uppercase tracking-[0.06em] text-[#6B6B6B]">{COPY.listLabel}</p>
                    <ul data-testid={`${testid}-items`} className="mt-1 list-disc space-y-1 pl-5 text-sm text-[#4A4A4A]">
                        {box.items.map((it) => <li key={it}>{it}</li>)}
                    </ul>
                </>
            )}
            <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1">
                <button type="button" data-testid={`${testid}-go`} data-cta={MOMENTS[moment]?.entry}
                    onClick={() => { evt('clicked'); onGo?.(); }}
                    className="min-h-[44px] rounded-full bg-[#1A4D2E] px-5 text-sm font-semibold text-[#FDFCF7] hover:bg-[#2D6B45] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#B4532F] focus-visible:ring-offset-2">
                    {box.button}
                </button>
                <button type="button" data-testid={`${testid}-not-now`}
                    onClick={() => { evt('dismissed'); dismissMoment(moment); setGone(true); }}
                    className="min-h-[44px] text-sm font-semibold text-[#4A4A4A] underline underline-offset-4 hover:text-[#1A4D2E] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#B4532F]">
                    {MOMENTS[moment]?.notNow || COPY.notNow}
                </button>
                {extra && (
                    <button type="button" data-testid={`${testid}-extra`} onClick={extra.onClick}
                        className="min-h-[44px] text-sm font-semibold text-[#1A4D2E] underline underline-offset-4 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#B4532F]">
                        {extra.label}
                    </button>
                )}
            </div>
        </section>
    );
}
